import React, { useRef, useState } from 'react';
import { AlertTriangle, Info  } from 'lucide-react';

import { FileUploadState, FileUploadStatus } from '../../models/FileUploadTypes';
import { formatDuration } from '../../utils/formatUtils';

interface FileInfoIconProps {
  fileState: FileUploadState;
  boundaryRef: React.RefObject<HTMLElement|null>;
}

const FileInfoIcon: React.FC<FileInfoIconProps>  = ({ fileState, boundaryRef }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [side, setSide] = useState<'right' | 'left'>('right');

  const handleMouseEnter = (): void => {
    const triggerEl = wrapperRef.current;
    const boundaryEl = boundaryRef.current;
    if (!triggerEl || !boundaryEl) return;

    const triggerRect = triggerEl.getBoundingClientRect();
    const boundaryRect = boundaryEl.getBoundingClientRect();
    const tooltipWidth = tooltipRef.current ? tooltipRef.current.offsetWidth : 256;
    const gap = 8;

    let nextSide: 'right' | 'left' = 'right';
    // Flip to left if going past right edge of boundary
    if (triggerRect.right + gap + tooltipWidth > boundaryRect.right) {
      nextSide = 'left';
    }
    // If left would overflow left edge, force right
    if (nextSide === 'left' && (triggerRect.left - gap - tooltipWidth) < boundaryRect.left) {
      nextSide = 'right';
    }
    setSide(nextSide);
  };

  return (
    <>
      { 
        (
          fileState.status === FileUploadStatus.RETRYING || 
          fileState.status === FileUploadStatus.FAILED
        ) &&
        (
          <div
            ref={wrapperRef}
            onMouseEnter={handleMouseEnter}
            data-testid="div-completed-file-icon"
            className="group relative flex items-center justify-center cursor-pointer"
            aria-label="File upload completed"
          >
            <Info className="w-5 h-5 text-orange-600" />
            <div
              ref={tooltipRef}
              className={`tooltip ${side === 'right' ? 'left-full ml-2 origin-left' : 'right-full mr-2 origin-right'}`}
              role="dialog"
              aria-label="Upload details"
            >
              <div className="font-semibold text-gray-800 mb-1">Upload Info</div>
              <ul className="space-y-1">
                <li>
                  <span className="font-medium">Started: </span>
                  {fileState.startTime ? new Date(fileState.startTime).toLocaleString() : '(unknown)'}
                </li>
                <li>
                  <span className="font-medium">Retry Attempts: </span>
                  {fileState.attemptCount}
                </li>
                <li>
                  <span className="font-medium">Error: </span>
                  {fileState.error}
                </li>
              </ul>
            </div>
          </div>
        )
      }

      { fileState.status === FileUploadStatus.COMPLETED &&
        (
          <div
            ref={wrapperRef}
            onMouseEnter={handleMouseEnter}
            data-testid="div-completed-file-icon"
            className="group relative flex items-center justify-center cursor-pointer"
            aria-label="File upload completed"
          >
            <Info className="w-5 h-5 text-blue-600" />
            <div
              ref={tooltipRef}
              className={`tooltip ${side === 'right' ? 'left-full ml-2 origin-left' : 'right-full mr-2 origin-right'}`}
              role="dialog"
              aria-label="Upload details"
            >
              <div className="font-semibold text-gray-800 mb-1">Upload Info</div>
              <ul className="space-y-1">
                <li>
                  <span className="font-medium">Started: </span>
                  {fileState.startTime ? new Date(fileState.startTime).toLocaleString() : '(unknown)'}
                </li>
                <li>
                  <span className="font-medium">Completed: </span>
                  {fileState.endTime ? new Date(fileState.endTime).toLocaleString() : '(unknown)'}
                </li>
                <li>
                  <span className="font-medium">Duration: </span>
                  {
                    (fileState.startTime && fileState.endTime) ?
                      formatDuration(Math.round((fileState.endTime - fileState.startTime) / 1000)) :
                      'unknown'
                  }
                </li>
              </ul>
            </div>
          </div>
        )
      }

      { (fileState.existingFile && fileState.status === 'PENDING') &&
        (
          <div
            ref={wrapperRef}
            onMouseEnter={handleMouseEnter}
            data-testid="div-existing-file-icon"
            className="group relative flex items-center justify-center cursor-pointer"
            aria-label="File already exists"
          >
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            <div
              ref={tooltipRef}
              className={`tooltip ${side === 'right' ? 'left-full ml-2 origin-left' : 'right-full mr-2 origin-right'}`}
              role="dialog"
              aria-label="Existing file details"
            >
              <div className="font-semibold text-gray-800 mb-1">Existing File Info</div>
              <ul className="space-y-1">
                <li>
                  <span className="font-medium">Name: </span>
                  {fileState.existingFile.filePathSansCaseFolder || '(unknown)'}
                </li>
                {fileState.existingFile.sizeDesc && (
                  <li>
                    <span className="font-medium">Size: </span>
                    {fileState.existingFile.sizeDesc}
                  </li>
                )}
                {fileState.existingFile.lastModified && (
                  <li>
                    <span className="font-medium">Last Modified: </span>
                    {new Date(fileState.existingFile.lastModified).toLocaleString()}
                  </li>
                )}
              </ul>
              <div className="mt-2 text-yellow-700">
                This upload may overwrite the existing file.
              </div>
            </div>
          </div>
        )
      }

    </>
  );
};
export default FileInfoIcon;
